import { StyleSheet, Platform } from 'react-native'

// Exact design-system colors from the reference
const C = {
  cardBg: '#1A1233',
  borderDefault: 'transparent',
  borderPicked: '#A78BFA',
  oddsBg: '#0F0A1F',
  oddsBorder: '#2A1F44',
  oddsAmber: '#F59E0B',
  primary: '#A78BFA',
  gold: '#FCD34D',
  green: '#6EE7B7',
  textDefault: '#F7FAFC',
  textDim: '#9CA3AF',
  textDark: '#111827',
  expandBg: 'rgba(167,139,250,0.04)',
  expandBorder: 'rgba(167,139,250,0.08)',
} as const

export const getGameInlineCardStyles = () =>
  StyleSheet.create({
    card: {
      backgroundColor: C.cardBg,
      borderRadius: 10,
      overflow: 'hidden',
      borderWidth: 1,
      borderColor: C.borderDefault,
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 2px 4px rgba(0,0,0,0.4)' as const }
        : {
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 2 },
            shadowOpacity: 0.4,
            shadowRadius: 4,
            elevation: 3,
          }),
    },
    cardPicked: {
      borderColor: C.borderPicked,
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 0 0 3px rgba(167,139,250,0.12), 0 2px 4px rgba(0,0,0,0.4)' as const }
        : {
            shadowColor: C.primary,
            shadowOffset: { width: 0, height: 0 },
            shadowOpacity: 0.3,
            shadowRadius: 6,
            elevation: 5,
          }),
    },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: 10,
      paddingVertical: 7,
      gap: 8,
    },
    teamsColumn: {
      flex: 1,
      gap: 2,
      minWidth: 0,
    },
    teamRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
    teamName: {
      fontSize: 11,
      fontWeight: '600' as const,
      color: C.textDefault,
      flex: 1,
    },
    headerRight: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
    gameTime: {
      fontSize: 11,
      color: C.textDim,
      fontVariant: ['tabular-nums'],
    },
    // "Estimated" pill — shown when odds are synthetic (not from a bookmaker)
    estimatedBadge: {
      paddingHorizontal: 6,
      paddingVertical: 2,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: 'rgba(245,158,11,0.5)',
      backgroundColor: 'rgba(245,158,11,0.12)',
    },
    /** Sport marker on a non-football card. Deliberately quieter than the "Est." badge — it
     *  identifies, it does not warn (specs/multi-sport-challenges). */
    sportBadge: {
      paddingHorizontal: 6,
      paddingVertical: 2,
      borderRadius: 6,
      borderWidth: 1,
      borderColor: 'rgba(167,139,250,0.45)',
      backgroundColor: 'rgba(167,139,250,0.12)',
    },
    sportBadgeText: {
      fontSize: 9,
      fontWeight: '700' as const,
      letterSpacing: 0.3,
      color: '#A78BFA',
      textTransform: 'uppercase' as const,
    },
    estimatedBadgeText: {
      fontSize: 9,
      fontWeight: '700' as const,
      letterSpacing: 0.3,
      color: C.oddsAmber,
      textTransform: 'uppercase' as const,
    },
    // Small count dot — shows number of bets picked for this game
    betCountDot: {
      width: 18,
      height: 18,
      borderRadius: 9999,
      backgroundColor: C.primary,
      alignItems: 'center',
      justifyContent: 'center',
    },
    betCountDotText: {
      fontSize: 10,
      fontWeight: '700' as const,
      color: C.textDark,
    },
    // 1X2 odds row
    oddsRow: {
      flexDirection: 'row',
      paddingHorizontal: 10,
      paddingBottom: 8,
      gap: 5,
    },
    oddsTile: {
      flex: 1,
      paddingVertical: 6,
      paddingHorizontal: 4,
      borderRadius: 8,
      backgroundColor: C.oddsBg,
      borderWidth: 1,
      borderColor: C.oddsBorder,
      alignItems: 'center',
      gap: 1,
      overflow: 'hidden',
      position: 'relative',
      minHeight: 44,
    },
    oddsTileSelected: {
      backgroundColor: C.primary,
      borderWidth: 0,
      ...(Platform.OS === 'web'
        ? { boxShadow: '0 2px 8px rgba(167,139,250,0.4)' as const }
        : {}),
    },
    oddsTileDisabled: {
      opacity: 0.45,
    },
    oddsTilePressed: {
      opacity: 0.7,
    },
    oddsSideLabel: {
      fontSize: 10,
      fontWeight: '700' as const,
      letterSpacing: 0.4,
      color: C.textDim,
      textTransform: 'uppercase' as const,
    },
    oddsSideLabelSelected: {
      color: 'rgba(17,24,39,0.65)',
    },
    oddsValue: {
      fontSize: 15,
      fontWeight: '700' as const,
      color: C.oddsAmber,
      fontVariant: ['tabular-nums'],
    },
    oddsValueSelected: {
      color: C.textDark,
    },
    // Community bar at bottom of each tile (green line)
    communityBar: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      height: 2,
      backgroundColor: C.green,
      opacity: 0.5,
    },
    // Expanded panel
    expandedPanel: {
      borderTopWidth: 1,
      borderTopColor: C.expandBorder,
      backgroundColor: C.expandBg,
      paddingHorizontal: 12,
      paddingVertical: 10,
      gap: 8,
    },
    loadingContainer: {
      paddingVertical: 20,
      alignItems: 'center',
    },
    noMarketsContainer: {
      paddingVertical: 12,
      alignItems: 'center',
    },
    noMarketsText: {
      fontSize: 13,
      color: C.textDim,
    },
  })
